import m, {logger} from '@bitstillery/mithril'
import {App, api, store} from '@bitstillery/common/app'
import {get_support, load_support_portraits} from '@bitstillery/common/lib/support'
import {init_theme} from '@bitstillery/common/lib/theme'
import {read_ssr_data, record_ssr_data} from '@bitstillery/common/lib/ssr_data'
import {is_nested_object} from '@bitstillery/common/lib/utils'

import {register_cms_catalog} from '@/cms/lib/catalog'
import {prefetch_cms_page_for_ssr, prewarm_cms_page_from_ssr} from '@/cms/runtime/page'
import {ready_cms_page_when_idle, ready_cms_pages_on_intent} from '@/cms/runtime/prefetch'
import {load_cms_fragments} from '@/cms/runtime/fragments'
import {config} from './lib/config'
import {prepare_state} from '@/lib/state'
import {init_tawk} from '@/lib/tawk'

import {DEFAULT_ROUTE, routes} from '@/routes'
import {models} from '@/models/models'
import hooks from '@/lib/api_hooks'

import {$m, $s} from './app_state'

export {$m, $s}

/** SSR→client handoff key: the server found the request's token to be no longer valid. */
const TOKEN_REJECTED = 'identity_token_rejected'

export class AppPortal extends App {
    async get_personal_offers() {
        const {result, success} = await api.get('portal/offers')
        // A non-2xx (e.g. a stale token's 401) resolves with the error body as `result`;
        // don't spread it.
        if (!success) return
        $s.offers.personal.splice(0, $s.offers.personal.length, ...result)
    }

    override async init() {
        super.init(config, models)

        register_cms_catalog()
        // Compile all published CMS fragments up-front (footer, breadcrumb menu, embeds) so layout
        // chrome renders on the first paint — SSR fetches once, the client peeks the SSR handoff.
        // Self-guards against failure so a broken fragment can't block boot.
        await load_cms_fragments()
        // Portraits for the support contact; `get_support()` reads them synchronously and the identity
        // flow below is its first caller, so they have to be cached before that runs.
        await load_support_portraits()
        // User has a token and is logged in; retrieve a fresh
        // Token from the backend, so we always start with up-to-date relation
        // Data in the portal.
        if ($s.identity.token) {
            if (globalThis.__SSR_MODE__) {
                // SSR renders with the browser's existing token; rotating it here
                // (portal/refresh-portal-token) is wasted because the fresh token can't be written back to
                // the browser (`sync_token_to_cookie` is a no-op on the server). Load the relation
                // data with the current token instead — one fewer round-trip per authenticated render.
                if (await $m.identity.new_token($s.identity.token)) {
                    // The personal offers are loaded in SSR mode and passed to the client.
                    // No need to reload them in the browser again.
                    await this.get_personal_offers()
                } else {
                    // The backend rejected the token, so render the anonymous page and tell the
                    // browser to drop it before it hydrates. Without the handoff the client boots
                    // authenticated against markup that isn't, discovers the rejection itself
                    // mid-render, and logs out on top of a half-hydrated DOM.
                    $s.identity.token = null
                    record_ssr_data(TOKEN_REJECTED, true)
                }
            } else {
                await $m.order.load_current_order()
                // A returning session never passes through `new_token` in the browser — that runs on
                // the login transition and in SSR — so this is the only place chat gets loaded for a
                // visitor who arrives already signed in.
                init_tawk()
            }
        } else {
            // Can't use m.route yet...
            if (!globalThis.__SSR_MODE__) {
                const params = new URLSearchParams(window.location.search)
                const price_list_uuid = params.get('price_list_uuid')
                if (price_list_uuid) {
                    await $m.identity.fetch_contact(price_list_uuid)
                }
            }
        }
        await this.init_language()
        $s.support.contact = get_support()

        // SSR: the shell chrome (ShellHeader's hero + breadcrumbs) renders before CmsDynamicPage
        // loads the page, so the page meta must be in `$s.page` before the first render — after
        // the token validation above, so `collapsed-authenticated` resolves against the real
        // auth state. No-op in the browser and on non-CMS routes.
        await prefetch_cms_page_for_ssr()

        // Only initialize client-side routing in the browser (not while we running in SSR mode)
        if (!globalThis.__SSR_MODE__) {
            // Re-apply a saved light/dark preference (no-op if the user is on `auto`).
            init_theme()

            // Compile the SSR-hydrated CMS page before the first render so CmsDynamicPage hydrates the
            // real component instead of flashing a Spinner over the server HTML (async blob compile).
            await prewarm_cms_page_from_ssr()

            const root = document.querySelector('.app')
            if (!root) {
                return
            }
            m.route.prefix = ''
            m.route(root, DEFAULT_ROUTE, routes)

            // Keep the cart in step with the customer's other tabs and devices.
            $m.order.reload_order_on_return()

            // Load CMS pages — module, copy and data — ahead of the click that needs them, so a first
            // navigation swaps in complete like a repeat one does instead of blanking and then filling
            // in section by section.
            ready_cms_pages_on_intent()
            // Every surface links home — including /shop and /my-portal, which are not CMS pages and so
            // leave its cache cold no matter how long the visitor stays.
            ready_cms_page_when_idle('/')
        }
    }
}

// Browser-side initialization that deals with SSR state and hydration.
if (!globalThis.__SSR_MODE__) {
    // Absent when the server served the bare client shell because SSR failed; render from scratch.
    const ssr_state_json = document.querySelector('#__SSR_STATE__')?.textContent ?? ''
    let serialized_state: Record<string, unknown> | undefined
    try {
        // The SSR server serializes the states into one JSON object.
        const parsed: unknown = JSON.parse(ssr_state_json)
        serialized_state = is_nested_object(parsed) ? parsed : undefined
    } catch {
        serialized_state = undefined
    }
    window.__SSR_STATE__ = serialized_state
    logger.info(`hydrate ssr state (${(ssr_state_json.length / 1024).toFixed(2)}KB)`)
    prepare_state({serializedState: serialized_state, source: 'hydration', store})
    api.load_hooks(hooks)

    // The server already rendered anonymously because it saw this token rejected; drop the copy
    // `store.load` just restored so init takes the anonymous branch and hydration matches.
    if (read_ssr_data(TOKEN_REJECTED)) {
        $m.identity.discard_token()
    }

    const app = new AppPortal()
    void app.init()
}
